// FUTURE: This entire folder can probably be refactored a good bit. Its kind of weird how we have different files for all the popup content.
// Could probably be simplified to something like popup-info.tsx and then have a single component that takes in the items and renders them.
// Also then in this folder for non vectors just allow some props instead of children

import { Box, Flex } from "@chakra-ui/react";
import { centerOfMass } from "@turf/turf";
import { LngLat, MapGeoJSONFeature } from "maplibre-gl";
import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { RanaHeading, RasterIcon, VectorIcon } from "@nens/ranalib";
import { objectFromEntries } from "@nens/shared-utils";

import { RanaTabs } from "@/lib/rana-tabs";
import { useFileDescriptorQuery } from "@/queries/query-file-descriptors";

import { layerHasTableMeta, useShowsOnlyGraph } from "../hooks/use-shows-only-graph";

import { VectorInfo } from "./vector-popup-info";

export type DisplayPopupInfoItem =
  | {
      title: string;
      features: MapGeoJSONFeature[];
      descriptorId: string;
      layerName: string;
      isVector: true;
    }
  | {
      title: string;
      children: ReactNode;
      isVector: false;
    };

type DisplayPopupInfoProps = {
  items: DisplayPopupInfoItem[];
  setPoint: (point: LngLat | null) => void;
  clickedPoint: LngLat | null;
  hasIcons?: boolean;
  // Space-constrained container (e.g. file preview maps): hide the location header and layer/feature tabs
  // when the popup would show nothing but a graph. Full screen maps (e.g. publications) can leave this unset.
  isCompact?: boolean;
};

const getFeatureCenter = (feature: MapGeoJSONFeature): LngLat => {
  const [lng, lat] = centerOfMass(feature).geometry.coordinates as [number, number];
  return new LngLat(lng, lat);
};

type TabTitleProps = {
  title: string;
  isVector: boolean;
  hasIcons?: boolean;
};

const TabTitle = ({ title, isVector, hasIcons }: TabTitleProps) => (
  <Flex as="span" alignItems="center" gap={2}>
    {hasIcons && isVector ? <VectorIcon /> : <RasterIcon />}
    {title}
  </Flex>
);

type VectorLayerPanelProps = {
  item: DisplayPopupInfoItem & { isVector: true };
  setPoint: (point: LngLat | null) => void;
};

const VectorLayerPanel = ({ item, setPoint }: VectorLayerPanelProps) => {
  const { data: descriptor } = useFileDescriptorQuery(item.descriptorId);
  const hasTable = layerHasTableMeta(descriptor, item.layerName);

  const handleFeatureTabChange = (idx: number) => {
    setPoint(getFeatureCenter(item.features[idx]));
  };

  // rendering the graph takes too much space already in the popup, so only render that
  if (hasTable) {
    return (
      <VectorInfo
        feature={item.features[0]}
        descriptorId={item.descriptorId}
        layerName={item.layerName}
      />
    );
  }

  // for multiple vector items rendered the RanaTabs
  const featureTabs = objectFromEntries(
    item.features.map((feature) => [
      feature.id + "",
      {
        title: feature.id,
        panel: (
          <VectorInfo
            key={feature.id}
            feature={feature}
            descriptorId={item.descriptorId}
            layerName={item.layerName}
          />
        ),
      },
    ])
  );

  return (
    <>
      <RanaHeading type="h6" mb={2}>
        <FormattedMessage id="feature_id" defaultMessage="Feature id" />
      </RanaHeading>
      <RanaTabs
        tabPanelProps={{ mt: 4, overflow: "auto", maxH: "sm" }}
        onChange={handleFeatureTabChange}
        tabs={featureTabs}
      />
    </>
  );
};

export const DisplayPopupInfo = ({
  items,
  setPoint,
  clickedPoint,
  hasIcons,
  isCompact,
}: DisplayPopupInfoProps) => {
  const showsOnlyGraph = useShowsOnlyGraph(items);
  const hideHeaderAndTabs = isCompact && showsOnlyGraph;

  const handleLayerTabChange = (idx: number) => {
    const item = items[idx];
    if (item.isVector) {
      setPoint(getFeatureCenter(item.features[0]));
    } else {
      setPoint(clickedPoint);
    }
  };

  const firstItem = items[0];
  if (hideHeaderAndTabs && firstItem.isVector) {
    return (
      <Box w="xl">
        <VectorLayerPanel item={firstItem} setPoint={setPoint} />
      </Box>
    );
  }

  const tabs = objectFromEntries(
    items.map((item) => [
      item.title,
      {
        title: <TabTitle title={item.title} isVector={item.isVector} hasIcons={hasIcons} />,
        panel: item.isVector ? <VectorLayerPanel item={item} setPoint={setPoint} /> : item.children,
      },
    ])
  );

  return (
    <Box w="xl">
      <RanaHeading type="h6" mb={2}>
        <FormattedMessage id="layer" defaultMessage="Layer" />
      </RanaHeading>
      <RanaTabs tabPanelProps={{ mt: 4 }} onChange={handleLayerTabChange} tabs={tabs} />
    </Box>
  );
};
